import { type FC, useState } from 'react'
import { Controller, type UseControllerProps, useFormContext } from 'react-hook-form'

import { HUMAN_DATE_FORMAT } from '@Constants/date'
import InputDate, { type InputDateProps } from '@DS/components/forms/input/InputDate/InputDate'

import { getFormattedDate, getValue } from './ControlInputDate.utils'

type Control = UseControllerProps<Record<string, InputDateProps['value']>, string>

type ControlInputDateProps = Omit<InputDateProps, 'name' | 'value' | 'onChange'> &
  Partial<Pick<InputDateProps, 'onChange'>> &
  Control

const ControlInputDate: FC<ControlInputDateProps> = ({
  defaultValue,
  name,
  rules,
  onClear,
  onChange,
  dateFormat = HUMAN_DATE_FORMAT,
  ...rest
}) => {
  const { control, setValue } = useFormContext()
  const [updateClear, setUpdateClear] = useState(0)

  const onClearInput = () => {
    setValue(name, '')
    setUpdateClear((prevUpdateClear) => prevUpdateClear + 1)
    onClear?.()
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: { value: fieldValue, onChange: onFieldChange, ref }, fieldState: { error } }) => (
        <InputDate
          key={updateClear}
          {...rest}
          ref={ref}
          onChange={(value) => {
            const formattedDate = getFormattedDate(value, dateFormat)
            onFieldChange(formattedDate)
            onChange?.(formattedDate)
          }}
          isRequired={!!rules?.required}
          onClear={onClearInput}
          name={name}
          isError={!!error}
          aria-invalid={!!error}
          value={getValue(fieldValue, dateFormat)}
          helperText={!!error && error.message}
          dateFormat={dateFormat}
        />
      )}
    />
  )
}

export default ControlInputDate
